import { useState, useCallback, useEffect } from 'react';
import { useQuery } from '@tanstack/react-query';
import { apiClient } from '@/lib/api-client';
import { Badge } from '@/components/ui/Badge';
import { useEntityTypes } from '@/features/settings/hooks/useEntityTypes';
import { useTags } from '@/features/contacts/hooks/useContacts';

// ─── Types ────────────────────────────────────────────────────────────────────

export interface ClientSearchResult {
  id: string;
  firstName: string | null;
  lastName: string | null;
  displayName: string;
  /** Alias for displayName — backward compatibility */
  fullName: string;
  email: string | null;
  phone: string | null;
  businessName: string | null;
  clientCode: string | null;
  contactNature: string;
  status: string;
  entityTypeName: string | null;
  assignedAccountantName: string | null;
  portalEmail: string | null;
  /**
   * The contact's default Practice Entity (code or id), if configured on the
   * contact form. Read from the real column when present, otherwise from the
   * dynamic form_data where the Form Builder field stores it.
   */
  practiceEntityId: string | null;
}

export interface ClientSearchModalProps {
  /** Called when a client is selected (single-select mode) */
  onSelect?: (client: ClientSearchResult) => void;
  /** Called when clients are confirmed (multi-select mode) */
  onConfirm?: (clients: ClientSearchResult[]) => void;
  /** Called when the modal is closed without selection */
  onClose: () => void;
  /** Modal title override */
  title?: string;
  /** Filter by contact nature (default: 'client') */
  contactNature?: string;
  /** Pre-selected client IDs (for multi-select mode) */
  selectedIds?: string[];
}

// ─── Component ────────────────────────────────────────────────────────────────

/**
 * Shared modal for searching and selecting a client/contact.
 *
 * Features:
 * - Inline type-ahead search (name, email, phone, business name)
 * - Filters: status (active/inactive/all), entity type, tags
 * - Paginated results
 * - Single-select and multi-select modes
 * - Accessible modal with keyboard support
 *
 * Used across: job creation, message creation, invoice creation, document sharing.
 */
export function ClientSearchModal({
  onSelect,
  onConfirm,
  onClose,
  title = 'Search & Select Client',
  contactNature = 'client',
  selectedIds = [],
}: ClientSearchModalProps) {
  const { data: entityTypes } = useEntityTypes();
  const { data: tags } = useTags();

  const [search, setSearch] = useState('');
  const [status, setStatus] = useState('active');
  const [nature, setNature] = useState(contactNature);
  const [entityTypeId, setEntityTypeId] = useState('');
  const [tagId, setTagId] = useState('');
  const [page, setPage] = useState(1);
  const [multiSelected, setMultiSelected] = useState<ClientSearchResult[]>([]);
  const isMultiMode = !!onConfirm;

  // Reset page when filters change
  useEffect(() => {
    setPage(1);
  }, [search, status, nature, entityTypeId, tagId]);

  // ─── Search query ──────────────────────────────────────────────────────────
  const { data, isLoading } = useQuery({
    queryKey: ['client-search-modal', search, status, nature, entityTypeId, tagId, page],
    queryFn: async () => {
      const params = new URLSearchParams();
      params.set('page', String(page));
      params.set('pageSize', '15');
      if (search) params.set('search', search);
      if (status) params.set('status', status);
      if (nature) params.set('contact_type_nature', nature);
      if (entityTypeId) params.set('entity_type_id', entityTypeId);
      if (tagId) params.set('tag_ids', tagId);

      const res = await apiClient.get<{
        success: boolean;
        data: Record<string, unknown>[];
        pagination: { page: number; pageSize: number; total: number };
      }>(`/contacts?${params.toString()}`);

      const items: ClientSearchResult[] = (res.data.data ?? []).map((c) => {
        const firstName = (c.first_name || '') as string;
        const lastName = (c.last_name || '') as string;
        const businessName = (c.business_name || null) as string | null;
        const fullName = [firstName, lastName].filter(Boolean).join(' ');
        const displayName = fullName || businessName || (c.email as string) || 'Unknown';

        // The Practice Entity field is added to the contact form via the Form
        // Builder; its value lands in form_data.practice_entity_id (falling back
        // to a real column if one exists).
        const formData = (c.form_data || c.formData || {}) as Record<string, unknown>;
        const practiceEntityId = ((c.practice_entity_id ??
          c.practiceEntityId ??
          formData.practice_entity_id ??
          null) ||
          null) as string | null;

        return {
          id: c.id as string,
          firstName: firstName || null,
          lastName: lastName || null,
          displayName,
          fullName: displayName,
          email: (c.email || null) as string | null,
          phone: (c.phone || c.mobile || null) as string | null,
          businessName,
          clientCode: (c.client_code || c.clientCode || null) as string | null,
          contactNature: (c.contact_nature || 'client') as string,
          status: (c.status || 'active') as string,
          entityTypeName: (c.entity_type_name || null) as string | null,
          assignedAccountantName: (c.assigned_accountant_name || null) as string | null,
          portalEmail: (c.portal_email || null) as string | null,
          practiceEntityId,
        };
      });

      return { items, pagination: res.data.pagination };
    },
    staleTime: 30_000,
  });

  const handleClientClick = useCallback(
    (client: ClientSearchResult) => {
      if (isMultiMode) {
        setMultiSelected((prev) => {
          const exists = prev.some((c) => c.id === client.id);
          if (exists) return prev.filter((c) => c.id !== client.id);
          return [...prev, client];
        });
      } else if (onSelect) {
        onSelect(client);
      }
    },
    [onSelect, isMultiMode],
  );

  const handleConfirm = useCallback(() => {
    if (onConfirm) {
      onConfirm(multiSelected);
    }
  }, [onConfirm, multiSelected]);

  const clients = data?.items ?? [];
  const pagination = data?.pagination;

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4"
      role="dialog"
      aria-modal="true"
      aria-labelledby="client-search-modal-title"
    >
      <div className="flex h-[80vh] w-full max-w-2xl flex-col rounded-xl bg-white shadow-2xl dark:bg-gray-900">
        {/* Header */}
        <div className="flex items-center justify-between border-b border-gray-200 px-6 py-4 dark:border-gray-700">
          <h2
            id="client-search-modal-title"
            className="text-lg font-semibold text-gray-900 dark:text-gray-100"
          >
            {title}
          </h2>
          <button
            type="button"
            onClick={onClose}
            className="rounded p-1 text-gray-400 hover:text-gray-700 dark:hover:text-gray-200"
            aria-label="Close"
          >
            <svg className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
              <path d="M6.28 5.22a.75.75 0 00-1.06 1.06L8.94 10l-3.72 3.72a.75.75 0 101.06 1.06L10 11.06l3.72 3.72a.75.75 0 101.06-1.06L11.06 10l3.72-3.72a.75.75 0 00-1.06-1.06L10 8.94 6.28 5.22z" />
            </svg>
          </button>
        </div>

        {/* Content */}
        <div className="flex flex-1 flex-col overflow-hidden">
          {/* Search + Filters */}
          <div className="border-b border-gray-200 bg-gray-50 px-6 py-3 dark:border-gray-700 dark:bg-gray-800/50">
            {/* Search input */}
            <div className="mb-3">
              <div className="relative">
                <svg
                  className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
                  />
                </svg>
                <input
                  type="text"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  placeholder="Type to search by name, code, email, phone, or business..."
                  className="w-full rounded-lg border border-gray-300 bg-white py-2 pl-9 pr-3 text-sm placeholder:text-gray-400 focus:border-brand-500 focus:outline-none focus:ring-1 focus:ring-brand-500 dark:border-gray-600 dark:bg-gray-800 dark:text-gray-100"
                  autoFocus
                />
              </div>
            </div>

            {/* Filter row */}
            <div className="flex flex-wrap gap-2">
              {/* Status — Active / Inactive / All */}
              <select
                value={status}
                onChange={(e) => setStatus(e.target.value)}
                className="rounded-md border border-gray-300 bg-white px-2 py-1.5 text-xs focus:outline-none focus:ring-1 focus:ring-brand-500 dark:border-gray-600 dark:bg-gray-800 dark:text-gray-200"
                aria-label="Filter by status"
              >
                <option value="">All</option>
                <option value="active">Active</option>
                <option value="inactive">Inactive</option>
              </select>

              {/* Contact Type */}
              <select
                value={nature}
                onChange={(e) => setNature(e.target.value)}
                className="rounded-md border border-gray-300 bg-white px-2 py-1.5 text-xs focus:outline-none focus:ring-1 focus:ring-brand-500 dark:border-gray-600 dark:bg-gray-800 dark:text-gray-200"
                aria-label="Filter by contact type"
              >
                <option value="">All Types</option>
                <option value="client">Client</option>
                <option value="prospect">Prospect</option>
              </select>

              {/* Entity Type */}
              <select
                value={entityTypeId}
                onChange={(e) => setEntityTypeId(e.target.value)}
                className="rounded-md border border-gray-300 bg-white px-2 py-1.5 text-xs focus:outline-none focus:ring-1 focus:ring-brand-500 dark:border-gray-600 dark:bg-gray-800 dark:text-gray-200"
                aria-label="Filter by entity type"
              >
                <option value="">All Entity Types</option>
                {(entityTypes ?? [])
                  .filter((et) => et.isActive)
                  .sort((a, b) => a.displayOrder - b.displayOrder)
                  .map((et) => (
                    <option key={et.id} value={et.id}>
                      {et.name}
                    </option>
                  ))}
              </select>

              {/* Tags */}
              <select
                value={tagId}
                onChange={(e) => setTagId(e.target.value)}
                className="rounded-md border border-gray-300 bg-white px-2 py-1.5 text-xs focus:outline-none focus:ring-1 focus:ring-brand-500 dark:border-gray-600 dark:bg-gray-800 dark:text-gray-200"
                aria-label="Filter by tag"
              >
                <option value="">All Tags</option>
                {(tags ?? []).map((tag) => (
                  <option key={tag.id} value={tag.id}>
                    {tag.name}
                  </option>
                ))}
              </select>
            </div>
          </div>

          {/* Results */}
          <div className="flex-1 overflow-y-auto">
            {isLoading ? (
              <div className="flex items-center justify-center py-12">
                <div className="h-5 w-5 animate-spin rounded-full border-2 border-brand-600 border-t-transparent" />
                <span className="ml-2 text-sm text-gray-500">Searching clients...</span>
              </div>
            ) : clients.length === 0 ? (
              <p className="py-12 text-center text-sm text-gray-500">
                No clients found. Try adjusting your search or filters.
              </p>
            ) : (
              <ul className="divide-y divide-gray-100 dark:divide-gray-700">
                {clients.map((client) => {
                  const isSelected =
                    isMultiMode &&
                    (multiSelected.some((c) => c.id === client.id) ||
                      selectedIds.includes(client.id));
                  return (
                    <li key={client.id}>
                      <button
                        type="button"
                        onClick={() => handleClientClick(client)}
                        className={`flex w-full items-start gap-4 px-6 py-3.5 text-left transition-colors ${
                          isSelected
                            ? 'bg-brand-50 dark:bg-brand-900/20'
                            : 'hover:bg-brand-50 dark:hover:bg-brand-900/10'
                        }`}
                      >
                        {/* Multi-select checkbox indicator */}
                        {isMultiMode && (
                          <div className="flex h-5 w-5 shrink-0 items-center justify-center pt-0.5">
                            <div
                              className={`h-4 w-4 rounded border ${
                                isSelected
                                  ? 'border-brand-600 bg-brand-600'
                                  : 'border-gray-300 dark:border-gray-600'
                              }`}
                            >
                              {isSelected && (
                                <svg
                                  className="h-4 w-4 text-white"
                                  viewBox="0 0 20 20"
                                  fill="currentColor"
                                >
                                  <path
                                    fillRule="evenodd"
                                    d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                                    clipRule="evenodd"
                                  />
                                </svg>
                              )}
                            </div>
                          </div>
                        )}

                        {/* Avatar */}
                        <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-brand-100 text-sm font-medium text-brand-700 dark:bg-brand-900 dark:text-brand-300">
                          {client.displayName.charAt(0).toUpperCase()}
                        </div>

                        <div className="min-w-0 flex-1">
                          <div className="flex flex-wrap items-center gap-2">
                            <span className="truncate font-medium text-gray-900 dark:text-gray-100">
                              {client.displayName}
                            </span>
                            {client.businessName && client.firstName && (
                              <span className="truncate text-xs text-gray-500 dark:text-gray-400">
                                ({client.businessName})
                              </span>
                            )}
                            {client.entityTypeName && (
                              <span className="rounded bg-gray-100 px-1.5 py-0.5 text-xs text-gray-600 dark:bg-gray-700 dark:text-gray-300">
                                {client.entityTypeName}
                              </span>
                            )}
                            {client.clientCode && (
                              <span className="rounded bg-brand-50 px-1.5 py-0.5 font-mono text-xs text-brand-700 dark:bg-brand-900/20 dark:text-brand-300">
                                {client.clientCode}
                              </span>
                            )}
                          </div>
                          <div className="mt-0.5 flex flex-wrap items-center gap-x-3 gap-y-0.5 text-xs text-gray-500 dark:text-gray-400">
                            {client.email && <span>{client.email}</span>}
                            {client.phone && <span>{client.phone}</span>}
                            {client.assignedAccountantName && (
                              <span>Accountant: {client.assignedAccountantName}</span>
                            )}
                          </div>
                        </div>

                        <div className="flex shrink-0 items-center gap-2">
                          <Badge
                            variant={client.status === 'active' ? 'success' : 'default'}
                            size="sm"
                          >
                            {client.status.charAt(0).toUpperCase() + client.status.slice(1)}
                          </Badge>
                          {!isMultiMode && (
                            <svg
                              className="h-4 w-4 text-gray-400"
                              viewBox="0 0 20 20"
                              fill="currentColor"
                            >
                              <path
                                fillRule="evenodd"
                                d="M7.21 14.77a.75.75 0 01.02-1.06L11.168 10 7.23 6.29a.75.75 0 111.04-1.08l4.5 4.25a.75.75 0 010 1.08l-4.5 4.25a.75.75 0 01-1.06-.02z"
                                clipRule="evenodd"
                              />
                            </svg>
                          )}
                        </div>
                      </button>
                    </li>
                  );
                })}
              </ul>
            )}
          </div>

          {/* Pagination */}
          {pagination && pagination.total > 15 && (
            <div className="flex items-center justify-between border-t border-gray-200 px-6 py-3 text-xs text-gray-500 dark:border-gray-700 dark:text-gray-400">
              <span>{pagination.total} clients found</span>
              <div className="flex gap-2">
                <button
                  type="button"
                  disabled={page <= 1}
                  onClick={() => setPage((p) => p - 1)}
                  className="rounded border border-gray-300 px-2 py-1 hover:bg-gray-50 disabled:opacity-40 dark:border-gray-600 dark:hover:bg-gray-800"
                >
                  Previous
                </button>
                <button
                  type="button"
                  disabled={page * 15 >= pagination.total}
                  onClick={() => setPage((p) => p + 1)}
                  className="rounded border border-gray-300 px-2 py-1 hover:bg-gray-50 disabled:opacity-40 dark:border-gray-600 dark:hover:bg-gray-800"
                >
                  Next
                </button>
              </div>
            </div>
          )}

          {/* Multi-select confirm button */}
          {isMultiMode && multiSelected.length > 0 && (
            <div className="flex items-center justify-between border-t border-gray-200 px-6 py-3 dark:border-gray-700">
              <span className="text-sm text-gray-600 dark:text-gray-400">
                {multiSelected.length} client{multiSelected.length > 1 ? 's' : ''} selected
              </span>
              <button
                type="button"
                onClick={handleConfirm}
                className="rounded-lg bg-brand-600 px-4 py-2 text-sm font-medium text-white hover:bg-brand-700"
              >
                Confirm Selection
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
